import { Button, Stack } from '@mui/material'
import { useWeb3React } from '@web3-react/core'
import { Currency } from 'api/v1/market'
import Loading from 'components/Loading'
import toastify from 'components/Toastify'
import { memo, useEffect, useMemo, useState } from 'react'
import { useCurrentNetwork } from 'store/app/hooks'
import { useSpotCurrencies } from 'store/market/hooks'
import { isGasAddress, isZeroAddress } from 'utils/address'
import { FaucetTips, GrayWrap } from './styles'
import { FaucetTokenSelection } from './TokenSelection'
import { useContracts } from '../../store/web3/hooks'
import { useEthereumTokenInfo } from '../../hooks/web3/useEthereumTokenInfo'
import { FAUCET_CONTRACT_INTERFACE } from '../../abi'
import { getEvmRecentConnection } from '../../connection/meta'
import { FlatNetworkSelection } from '../../components/FlatNetworkSelection'
import { WalletNames } from '../../connection/utils'

export const FaucetLayer1 = memo(() => {
  const currentNetwork = useCurrentNetwork()
  const [claiming, setClaiming] = useState(false)
  const [selectedToken, setSelectedToken] = useState<Currency>()
  const tokenList = useSpotCurrencies(currentNetwork?.layerTwoChainId)
  const contracts = useContracts()
  const { provider } = useWeb3React()

  const faucetTokenList = useMemo(() => {
    return tokenList.filter((item) => {
      const _tokenAddress = item.chains[currentNetwork.layerTwoChainId]?.address
      if (!_tokenAddress) return false
      return !isGasAddress(_tokenAddress) && !isZeroAddress(_tokenAddress)
    })
  }, [tokenList, currentNetwork])

  const token = useEthereumTokenInfo(
    currentNetwork.layerOneChainId,
    selectedToken?.l2CurrencyId!,
  )

  useEffect(() => {
    if (faucetTokenList?.length) {
      setSelectedToken(faucetTokenList[0])
    }
  }, [faucetTokenList])

  const onHandleClaim = async () => {
    if (claiming) {
      return
    }
    if (!selectedToken?.name) {
      toastify.warn('Select a token first')
      return
    }
    if (!provider) {
      toastify.error('Connect wallet first')
      return
    }
    if (!token) {
      toastify.error('Token not found')
      return
    }
    setClaiming(true)

    try {
      const _contractAddress = contracts.faucet[currentNetwork.layerOneChainId]
      const { address } = token

      const signer = provider.getSigner()
      const data = FAUCET_CONTRACT_INTERFACE.encodeFunctionData('mint', [
        address,
      ])
      const tx = {
        from: await signer.getAddress(),
        to: _contractAddress,
        data: data,
      }
      const r = await signer.sendTransaction(tx).catch((e) => {
        if (e.reason) {
          throw new Error(e.reason)
        }
        throw e
      })
      if (r.hash) {
        toastify.success(
          'Claim success, please wait for the transaction to complete',
        )
      }
    } catch (e) {
      toastify.error(e.message)
    } finally {
      setClaiming(false)
    }
  }

  const { name = '' } = getEvmRecentConnection()?.getProviderInfo() ?? {
    name: WalletNames.METAMASK,
  }

  return (
    <Stack spacing={2}>
      <GrayWrap spacing={1}>
        <FaucetTips>
          Faucet tokens will be claimed into your <strong>{name}</strong>
          wallet. (~90 sec)
        </FaucetTips>
        <FlatNetworkSelection />
        <FaucetTokenSelection
          tokenList={faucetTokenList}
          selectedToken={selectedToken!}
          onChangeToken={(token) => setSelectedToken(token)}
        />
      </GrayWrap>
      <Button
        sx={{ gap: 0.5 }}
        variant="contained"
        fullWidth={true}
        className={selectedToken?.name && !claiming ? 'active' : 'disable'}
        onClick={() => onHandleClaim()}
      >
        {claiming ? <Loading /> : null}
        <span>
          {claiming ? 'claiming' : `Claim on ${currentNetwork?.name}`}
        </span>
      </Button>
    </Stack>
  )
})
